import type { ApolloError } from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';
import authTokenUtil from '../../account/authTokenUtil';
import localLogout from '../../account/localLogout';
import visitorAuthTokenUtil from '../../account/visitorAuthTokenUtil';
import Logger from '../Logger';
import { isPublicViewPath } from '../useIsPublicView';
import getNetworkErrorExtra from './getNetworkErrorExtra';
import parseGraphQLError from './parseGraphQLError';

const LOGGING_IGNORED_QUERIES = ['allTopicsWithPosts', 'currentVersion'];

function handleUnauthorizedError() {
  const isPublicView = isPublicViewPath(window.location.pathname);

  if (isPublicView) {
    const isVisitorSession = !authTokenUtil.get() && visitorAuthTokenUtil.get();

    if (isVisitorSession) {
      visitorAuthTokenUtil.remove();
    } else {
      localLogout({
        preventRedirect: true,
      });
    }
    window.location.reload();
  } else {
    localLogout();
  }
}

export type ValidationErrorHandler = () => Promise<{
  isVersionMismatchModalOpen: boolean;
}>;

export default function handleUnknownApolloErrors({
  error,
  prefix,
  handledCodes,
  handleValidationError,
}: {
  error: ErrorResponse | ApolloError;
  prefix: string;
  handledCodes?: string[];
  handleValidationError?: ValidationErrorHandler;
}) {
  const { graphQLErrors, networkError } = error;

  if (graphQLErrors?.find((e) => e.message === 'UNAUTHORIZED')) {
    handleUnauthorizedError();
    return;
  }

  const { operationName, query, variables } =
    'operation' in error
      ? error.operation
      : { operationName: null, query: null, variables: null };

  if (operationName && LOGGING_IGNORED_QUERIES.includes(operationName)) {
    return;
  }

  const definition = query?.definitions[0];
  const operationType =
    definition && 'operation' in definition ? definition.operation : '';
  const operationMessage = operationName
    ? `: ${operationType} ${operationName}`
    : '';

  if (graphQLErrors) {
    graphQLErrors
      .filter((e) => {
        const { code } = parseGraphQLError(e);
        if (code && handledCodes?.includes(code)) {
          return false;
        }

        return !('code' in e) || typeof e.code !== 'number' || e.code >= 500;
      })
      .forEach((e) => {
        (async () => {
          let isVersionMismatchModalOpen = false;
          if (
            handleValidationError &&
            e.message.startsWith('Cannot query field')
          ) {
            try {
              const result = await handleValidationError();
              isVersionMismatchModalOpen = result.isVersionMismatchModalOpen;
            } catch (validationHandlerError) {
              Logger.error(validationHandlerError.message, {
                originalError: e.message,
              });
            }
          }

          const log = `${e.message}${operationMessage}`;

          Logger.warn(
            `[${prefix} GQL] ${log}`,
            {
              code: 'code' in e ? e.code : undefined,
              variables,
            },
            {
              fingerprint: log,
              tags: {
                isVersionMismatchModalOpen,
              },
            },
          );
        })();
      });
  }

  if (networkError && 'statusCode' in networkError) {
    if (
      networkError.statusCode === 401 &&
      'result' in networkError &&
      typeof networkError.result !== 'string' &&
      networkError.result.message === 'invalid_token'
    ) {
      handleUnauthorizedError();
      return;
    }

    if (networkError.statusCode >= 500) {
      const log = `${networkError.statusCode} ${networkError.message}${operationMessage}`;

      Logger.warn(
        `[${prefix} Network] ${log}`,
        {
          ...getNetworkErrorExtra(networkError),
          variables,
        },
        { fingerprint: log },
      );
    }
  }
}
